/**
 * Guest-side frame decode hint collector.
 *
 * On Chromium-class browsers, requestVideoFrameCallback on the remote video element reports
 * the RTP timestamp of each presented frame. Recording (rtpTimestamp, decodeWallMs) per frame
 * is what lets the HOST correlate a guest's decode time with the frame's recorded send time
 * and compute a real glass-to-glass latency.
 *
 * Safari/WebKit often omits rtpTimestamp; Phase 5 still records captureTime/receiveTime/
 * processingDuration when present so the guest can emit labelled `rvfc-capture-time` or
 * `estimate` reports instead of silently falling back.
 */

import type { FrameDecodeHint } from './frameLatencyTracker';
import type { GuestLatencyCapability } from './guestBrowserCapability';
import { probeGuestLatencyCapability } from './guestBrowserCapability';
import { PresentationTimestampMapper } from './measurement/PresentationTimestampMapper';
import type { FrameTimingSource } from './streamRecovery/contracts';

export type RvfcVideoMetadata = {
  rtpTimestamp?: number;
  presentedFrames?: number;
  captureTime?: number;
  receiveTime?: number;
  processingDuration?: number;
  presentationTime?: number;
  expectedDisplayTime?: number;
};

export type RvfcVideoElement = {
  requestVideoFrameCallback?: (
    cb: (now: number, metadata: RvfcVideoMetadata) => void,
  ) => number;
  cancelVideoFrameCallback?: (handle: number) => void;
  getVideoPlaybackQuality?: () => { droppedVideoFrames: number; totalVideoFrames: number };
};

export type DecodeHintCollectorOptions = {
  nowMs?: () => number;
  monotonicNowMs?: () => number;
  /** Keep at most this many hints buffered between wire sends. */
  maxBufferedHints?: number;
  /** Invoked once with the first observed rVFC metadata capability probe. */
  onCapability?: (capability: GuestLatencyCapability) => void;
  /**
   * Invoked for every presented frame.
   *
   * Exists so input-to-photon correlation can observe presentations without registering a
   * second rVFC loop on the same element — two loops on one video would double the
   * per-frame work and give the two consumers different views of the same frame.
   */
  onFrame?: (observation: LatestRvfcObservation) => void;
};

/** Latest rVFC observation used by the Safari/Chromium latency strategy chain. */
export type LatestRvfcObservation = {
  observationSequence: number;
  observationAgeMs: number;
  timingSource: FrameTimingSource;
  rtpTimestamp?: number;
  decodeWallMs: number;
  captureTime?: number;
  receiveTime?: number;
  processingDuration?: number;
  displayNow?: number;
  /** rVFC metadata: when the frame was submitted for composition (ms). */
  presentationTime?: number;
  /** rVFC metadata: when the compositor expects the frame on glass (ms). */
  expectedDisplayTime?: number;
  /** rVFC metadata: the compositor's cumulative presented-frame count for this element. */
  presentedFrames?: number;
};

export class DecodeHintCollector {
  private readonly nowMs: () => number;
  private readonly maxBufferedHints: number;
  private readonly onCapability?: (capability: GuestLatencyCapability) => void;
  private readonly onFrameObserver?: (observation: LatestRvfcObservation) => void;
  private hints: { hint: FrameDecodeHint; acceptedAtMs: number }[] = [];
  private rvfcHandle: number | null = null;
  private video: RvfcVideoElement | null = null;
  private running = false;
  private latestObservation: LatestRvfcObservation | null = null;
  private capabilityLogged = false;
  private readonly monotonicNowMs: () => number;
  private readonly timestampMapper: PresentationTimestampMapper;
  private observationSequence = 0;
  private observedAtMs: number | undefined;
  private lastFrameKey: string | undefined;

  constructor(options: DecodeHintCollectorOptions = {}) {
    this.nowMs = options.nowMs ?? (() => Date.now());
    this.monotonicNowMs = options.monotonicNowMs ?? (() => performance.now());
    this.timestampMapper = new PresentationTimestampMapper({ wallNowMs: this.nowMs, monotonicNowMs: this.monotonicNowMs });
    this.maxBufferedHints = options.maxBufferedHints ?? 8;
    this.onCapability = options.onCapability;
    this.onFrameObserver = options.onFrame;
  }

  /**
   * Attach to a remote video element and begin collecting; idempotent.
   *
   * Attaching implies intent to collect, so this starts the loop rather than requiring a
   * separate `start()`. The two used to be separate and `ensureLoop` bails while `running`
   * is false, so an `attach()` without a matching `start()` produced no hints at all — a
   * silent dead end rather than an error.
   */
  attach(video: RvfcVideoElement | null): void {
    if (this.video === video) {
      if (video) this.start();
      return;
    }
    // Swapping elements (React remount, stream reattach): the pending callback belongs to
    // the OLD element and never fires once that element stops painting. Releasing it here
    // matters because `ensureLoop` no-ops while a handle is outstanding, so a stale handle
    // would keep the collector dark against the new element forever.
    this.cancelPending();
    this.video = video;
    this.lastFrameKey = undefined;
    if (!video) return;
    this.start();
  }

  /**
   * Begin collecting. Called by attach; safe to call on its own once a video exists.
   *
   * Deliberately re-arms even when already running: `ensureLoop` is guarded by the
   * outstanding-handle check, so the work is idempotent, and an early return here would
   * skip arming the loop after a mid-session element swap.
   */
  start(): void {
    this.running = true;
    this.ensureLoop();
  }

  stop(): void {
    this.running = false;
    this.cancelPending();
  }

  /** Release any outstanding rVFC registration so the loop can be re-armed. */
  private cancelPending(): void {
    if (this.video?.cancelVideoFrameCallback && this.rvfcHandle !== null) {
      this.video.cancelVideoFrameCallback(this.rvfcHandle);
    }
    this.rvfcHandle = null;
  }

  /** Take the buffered hints, emptying the buffer. Called once per wire interval. */
  drain(): FrameDecodeHint[] {
    if (this.hints.length === 0) return [];
    const out = this.hints.map((entry) => this.agedHint(entry));
    this.hints = [];
    return out;
  }

  /**
   * Latest buffered hint without draining. Used by calibrated latency reports so a
   * concurrent clientStats drain (or lack of one) cannot steal the correlation key.
   */
  peekLatest(): FrameDecodeHint | undefined {
    if (this.hints.length === 0) return undefined;
    return this.agedHint(this.hints[this.hints.length - 1]);
  }

  /** Alias for peekLatest — readable at call sites that want "current frame". */
  latest(): FrameDecodeHint | undefined {
    return this.peekLatest();
  }

  /** Full latest rVFC observation (including Safari-only fields). */
  peekLatestObservation(): LatestRvfcObservation | null {
    if (!this.latestObservation) return null;
    return { ...this.latestObservation, observationAgeMs: this.latestObservation.observationAgeMs +
      Math.max(0, this.monotonicNowMs() - (this.observedAtMs ?? this.monotonicNowMs())) };
  }

  /** The attached element's cumulative getVideoPlaybackQuality counters, when the engine has them. */
  playbackQuality(): { droppedVideoFrames: number; totalVideoFrames: number } | undefined {
    try {
      return this.video?.getVideoPlaybackQuality?.();
    } catch {
      return undefined;
    }
  }

  private ensureLoop(): void {
    if (!this.running || !this.video?.requestVideoFrameCallback) return;
    if (this.rvfcHandle !== null) return;
    this.rvfcHandle = this.video.requestVideoFrameCallback((now, meta) =>
      this.onFrame(now, meta),
    );
  }

  private onFrame(now: number, meta: RvfcVideoMetadata): void {
    if (!this.capabilityLogged) {
      this.capabilityLogged = true;
      this.onCapability?.(probeGuestLatencyCapability(meta));
    }

    const mapped = this.timestampMapper.map(meta);
    const decodeWallMs = mapped.presentationWallMs;
    const frameKey = [meta.rtpTimestamp, meta.presentedFrames, meta.presentationTime].some((value) => typeof value === 'number')
      ? JSON.stringify([meta.rtpTimestamp, meta.presentedFrames, meta.presentationTime]) : undefined;
    const duplicate = frameKey !== undefined && frameKey === this.lastFrameKey;
    if (duplicate) { this.rvfcHandle = null; this.ensureLoop(); return; }
    this.lastFrameKey = frameKey;
    this.observationSequence += 1;
    this.observedAtMs = this.monotonicNowMs();
    this.latestObservation = {
      decodeWallMs,
      observationSequence: this.observationSequence,
      observationAgeMs: mapped.observationAgeMs,
      timingSource: mapped.timingSource,
      displayNow: now,
      ...(mapped.timingSource === 'presentation-time'
        ? { presentationTime: meta.presentationTime }
        : {}),
      ...(typeof meta?.expectedDisplayTime === 'number' && Number.isFinite(meta.expectedDisplayTime)
        ? { expectedDisplayTime: meta.expectedDisplayTime }
        : {}),
      ...(typeof meta?.rtpTimestamp === 'number' && Number.isFinite(meta.rtpTimestamp)
        ? { rtpTimestamp: meta.rtpTimestamp }
        : {}),
      ...(typeof meta?.captureTime === 'number' && Number.isFinite(meta.captureTime)
        ? { captureTime: meta.captureTime }
        : {}),
      ...(typeof meta?.receiveTime === 'number' && Number.isFinite(meta.receiveTime)
        ? { receiveTime: meta.receiveTime }
        : {}),
      ...(typeof meta?.processingDuration === 'number' && Number.isFinite(meta.processingDuration)
        ? { processingDuration: meta.processingDuration }
        : {}),
      ...(typeof meta?.presentedFrames === 'number' && Number.isFinite(meta.presentedFrames)
        ? { presentedFrames: meta.presentedFrames }
        : {}),
    };

    this.onFrameObserver?.(this.latestObservation);

    // Chromium path: buffer RTP-keyed hints for clientStats frameDecodeHints.
    const rtpTimestamp = meta?.rtpTimestamp;
    if (typeof rtpTimestamp === 'number' && Number.isFinite(rtpTimestamp)) {
      this.hints.push({ hint: { rtpTimestamp, decodeWallMs, observationSequence: this.observationSequence,
        observationAgeMs: mapped.observationAgeMs, timingSource: mapped.timingSource }, acceptedAtMs: this.observedAtMs });
      if (this.hints.length > this.maxBufferedHints) this.hints.shift();
    }

    // rVFC is one-shot; re-register for the next frame.
    this.rvfcHandle = null;
    if (this.video?.requestVideoFrameCallback && this.running) {
      this.rvfcHandle = this.video.requestVideoFrameCallback((n, m) => this.onFrame(n, m));
    }
  }

  /** Discard old-route correlations while retaining the session's monotonically increasing sequence. */
  resetForPath(): void {
    this.hints = []; this.latestObservation = null; this.observedAtMs = undefined; this.lastFrameKey = undefined;
  }

  private agedHint(entry: { hint: FrameDecodeHint; acceptedAtMs: number }): FrameDecodeHint {
    return { ...entry.hint, observationAgeMs: (entry.hint.observationAgeMs ?? 0) +
      Math.max(0, this.monotonicNowMs() - entry.acceptedAtMs) };
  }
}
